<script setup lang="ts">
import {computed} from 'vue';
import {formatAmountDisplay, formatAmountTooltip} from '@/ts/AmountDisplay';

interface Props {
    value: number | string | null | undefined;
    decimals?: number;
    minDecimals?: number;
    maxDecimals?: number;
    showTooltip?: boolean;
    negativeClass?: string;
    as?: string;
    currency?: string;
    displayCurrency?: boolean;
    displayFormat?: 'accounting' | 'currency';
    grouping?: boolean;
    fallback?: string;
    fallbackValue?: number;
}

const props = withDefaults(defineProps<Props>(), {
    decimals: 2,
    showTooltip: true,
    negativeClass: 'text-red-500',
    as: 'span',
    currency: 'Rs.',
    displayCurrency: false,
    displayFormat: 'currency',
    grouping: true,
    fallback: '-',
    fallbackValue: 0,
});

const parsedValue = computed<number | null>(() => {
    const raw = props.value;

    if (raw === null || raw === undefined) return null;

    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? raw : null;
    }

    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed === '') return null;

        const cleaned = trimmed.replace(/[^0-9.\-()]/g, '');
        if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;

        const isParenNegative = /^\(.*\)$/.test(cleaned);
        const unwrapped = isParenNegative ? cleaned.slice(1, -1) : cleaned;

        const num = Number(unwrapped);
        if (!Number.isFinite(num)) return null;

        return isParenNegative ? -Math.abs(num) : num;
    }

    return null;
});

const isInvalid = computed(() => parsedValue.value === null);

const safeValue = computed(() => (isInvalid.value ? props.fallbackValue : (parsedValue.value as number)));

const isNegative = computed(() => !isInvalid.value && safeValue.value < 0);

const finalDisplayString = computed(() => {
    if (isInvalid.value) return props.fallback;

    try {
        const formatted = formatAmountDisplay(safeValue.value, {
            decimals: props.decimals,
            minDecimals: props.minDecimals,
            maxDecimals: props.maxDecimals,
            displayFormat: props.displayFormat,
            grouping: props.grouping,
        });
        return formatted && formatted !== 'NaN' ? formatted : props.fallback;
    } catch {
        return props.fallback;
    }
});

const tooltipDisplayString = computed(() => {
    if (!props.showTooltip) return undefined;
    if (isInvalid.value) return props.fallback;

    try {
        const tooltip = formatAmountTooltip(safeValue.value, {
            decimals: props.decimals,
            displayFormat: props.displayFormat,
        });
        return tooltip && tooltip !== 'NaN' ? tooltip : props.fallback;
    } catch {
        return props.fallback;
    }
});
</script>

<template>
    <component :is="as" :class="{ [negativeClass]: isNegative }" :title="tooltipDisplayString">
        <span v-if="displayCurrency && !isInvalid" class="currency-symbol">{{ currency }} </span>
        <span class="amount-value">{{ finalDisplayString }}</span>
    </component>
</template>
